<template>
  <footer id="footer" ref="footer" class="bg-brand-cream text-gray-900 will-change-transform">
    <!-- Woven gamosa border, like the end of a gamosa -->
    <div class="gamosa-band" aria-hidden="true"></div>
    <div class="max-w-7xl mx-auto px-6 sm:px-8 pt-12 pb-8">
      <div ref="columns" class="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 text-center md:text-left">

        <!-- Logo & About -->
        <div class="md:col-span-5">
          <h2 class="font-serif text-3xl font-bold text-brand-red leading-tight">সাংস্কৃতিক দাপোণ</h2>
          <p class="mt-4 text-[15px] leading-relaxed text-gray-700 max-w-sm mx-auto md:mx-0">
            Stay updated with the latest news, trends, and insights from around the world.
          </p>
        </div>

        <!-- Quick Links -->
        <div class="md:col-span-3">
          <h3 class="font-serif text-xl font-bold text-brand-red">Quick Links</h3>
          <ul class="mt-4 space-y-2.5 text-[15px] text-gray-800">
            <li>
              <router-link to="/" class="footer-link">
                Home
              </router-link>
            </li>
            <li>
              <router-link to="/gallery" class="footer-link">
                Gallery
              </router-link>
            </li>
            <li>
              <router-link to="/about" class="footer-link">
                About Us
              </router-link>
            </li>
            <li>
              <router-link to="/suggestion" class="footer-link">
                Suggestion
              </router-link>
            </li>
            <li>
              <router-link to="/contact" class="footer-link">
                Contact Us
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Subscribe -->
        <div class="md:col-span-4">
          <h3 class="font-serif text-xl font-bold text-brand-red">Subscribe</h3>
          <p class="mt-4 text-[15px] text-gray-700">
            Join our newsletter for daily news updates.
          </p>
          <div class="mt-5 flex justify-center md:justify-start">
            <router-link to="/subscribe" class="btn-primary px-7">
              Subscribe
              <i class="fa-solid fa-arrow-right text-xs"></i>
            </router-link>
          </div>
        </div>
      </div>

      <div class="mt-12 pt-6 border-t border-black/10 flex flex-col items-center justify-between gap-2 text-center text-gray-600 text-sm md:flex-row md:text-left">
        <span>&copy; {{ year }} সাংস্কৃতিক দাপোণ. All Rights Reserved.</span>
        <!-- Developer credit: plain text look, only the cursor changes -->
        <a :href="PORTFOLIO_URL" target="_blank" rel="noopener" class="developer-credit">
          Developed by <span class="font-semibold text-gray-800">Nibir Borkataki</span>
        </a>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const year = new Date().getFullYear()
// Developer credit in the footer — change this to your portfolio address
const PORTFOLIO_URL = 'https://nibir-portfolio-pi.vercel.app/'
const footer = ref(null)
const columns = ref(null)
let ctx

onMounted(() => {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  ctx = gsap.context(() => {
    // Fires when the end of the page content reaches the bottom of the screen,
    // i.e. the moment the footer starts to appear. Replays on every scroll down.
    const trigger = {
      trigger: footer.value.previousElementSibling,
      start: 'bottom bottom',
      toggleActions: 'play none none reset',
    }

    // Footer pushes up from below, overshoots and settles (the "jump")
    gsap.fromTo(footer.value, { y: 140 }, {
      y: 0,
      duration: 1.5,
      ease: 'elastic.out(1, 0.55)',
      scrollTrigger: trigger,
    })

    // Columns follow a beat later
    gsap.fromTo(columns.value.children, { y: 30, opacity: 0 }, {
      y: 0,
      opacity: 1,
      duration: 0.7,
      delay: 0.15,
      stagger: 0.12,
      ease: 'power3.out',
      scrollTrigger: trigger,
    })
  })
})

onBeforeUnmount(() => {
  if (ctx) ctx.revert()
})
</script>

<style scoped>
.footer-link {
  transition: color 0.2s ease;
}
.footer-link:hover {
  color: #d84040;
}

/* Looks like normal footer text: no underline, no hover change — just a pointer cursor */
.developer-credit,
.developer-credit:hover,
.developer-credit:focus {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

.gamosa-band {
  height: 52px;
  background-image: url('@/assets/gamosa/border.svg');
  background-repeat: repeat-x;
  background-size: auto 100%;
  background-position: center;
  border-bottom: 1px solid rgba(198, 46, 46, 0.15);
}
@media (max-width: 640px) {
  .gamosa-band {
    height: 40px;
  }
}
</style>
